/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-09-27
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { useState, useEffect } from 'react';
import {
  useGetPaymentMethodsQuery,
  useUpdateBillingAddressMutation,
} from '../../../../features/payment/paymentApi';
import InternationalAddressForm, {
  InternationalAddressData,
} from '../../../common/InternationalAddressForm';

interface UpdateBillingAddressModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Modal for updating billing address information
 * Shows the current billing address and allows editing
 */
const UpdateBillingAddressModal: React.FC<UpdateBillingAddressModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  // Get payment methods to find current billing address
  const { data: paymentMethods, isLoading: paymentMethodsLoading } =
    useGetPaymentMethodsQuery();

  // Billing address update mutation
  const [updateBillingAddress] = useUpdateBillingAddressMutation();

  // Form state - simplified for billing address only
  const [cardholderName, setCardholderName] = useState('');
  const [address, setAddress] = useState<InternationalAddressData>({
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States',
  });
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Get default payment method and its billing address
  const defaultPaymentMethod = paymentMethods?.results?.find(
    pm => pm.is_default
  );
  // Note: billing_address should be available after backend changes
  const currentBillingAddress = (defaultPaymentMethod as any)?.billing_address;

  // Populate form with current billing address data
  useEffect(() => {
    if (currentBillingAddress) {
      setCardholderName(currentBillingAddress.name || '');
      setAddress({
        line1: currentBillingAddress.line1 || '',
        line2: currentBillingAddress.line2 || '',
        city: currentBillingAddress.city || '',
        state: currentBillingAddress.state || '',
        postalCode: currentBillingAddress.postal_code || '',
        country:
          currentBillingAddress.country === 'US'
            ? 'United States'
            : currentBillingAddress.country || 'United States',
      });
    }
  }, [currentBillingAddress]);

  // Reset form when modal closes
  const handleClose = () => {
    if (!isUpdating) {
      setError(null);
      onClose();
    }
  };

  // Simple form validation
  const isValid =
    cardholderName.trim().length > 0 &&
    address.line1.trim().length > 0 &&
    address.city.trim().length > 0 &&
    address.state.trim().length > 0 &&
    address.postalCode.trim().length > 0;

  // Handle form submission
  const handleUpdate = async () => {
    if (!defaultPaymentMethod?.id) {
      setError('No payment method found to update billing address.');
      return;
    }

    setError(null);
    setIsUpdating(true);

    try {
      // Call the real API endpoint
      await updateBillingAddress({
        paymentMethodId: defaultPaymentMethod.id,
        billingData: {
          name: cardholderName,
          line1: address.line1,
          line2: address.line2 || '',
          city: address.city,
          state: address.state,
          postal_code: address.postalCode,
          country: address.country === 'United States' ? 'US' : address.country,
        },
      }).unwrap();

      setIsUpdating(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Billing address update error:', err);

      // Handle different types of API errors
      let errorMessage = 'Failed to update billing address. Please try again.';

      if (err?.data?.error) {
        errorMessage = err.data.error;
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setIsUpdating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' },
      }}
    >
      <DialogTitle>
        Update Billing Address
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Update the billing address for your default payment method
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {paymentMethodsLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading current billing address...
            </Typography>
          </Box>
        )}

        {!paymentMethodsLoading && !currentBillingAddress && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>No billing address found:</strong> You don&apos;t have a
              billing address on file yet. Please add a payment method first to
              create a billing address.
            </Typography>
          </Alert>
        )}

        {!paymentMethodsLoading && currentBillingAddress && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Cardholder Name */}
            <TextField
              fullWidth
              label="Cardholder Name"
              value={cardholderName}
              onChange={e => setCardholderName(e.target.value)}
              placeholder="John Doe"
              disabled={isUpdating}
              required
              sx={{ mt: 1 }}
            />

            {/* Billing Address */}
            <InternationalAddressForm
              value={address}
              onChange={setAddress}
              sectionTitle="Billing Address"
              required
              requirePostalCode
              showGuidance={false}
              disabled={isUpdating}
              compact
            />

            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong> Updates to your billing address will be
                applied to your default payment method and used for future
                billing.
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={!isValid || isUpdating || !currentBillingAddress}
          startIcon={isUpdating ? <CircularProgress size={20} /> : undefined}
        >
          {isUpdating ? 'Updating...' : 'Update Billing Address'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateBillingAddressModal;
